import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import TradingViewWidget from '@/components/TradingViewWidget';
import { 
  Building2, TrendingUp, TrendingDown, Home, Building, Warehouse,
  Eye, Plus, Trash2, RefreshCw, DollarSign, BarChart3, PieChart,
  ArrowUpRight, ArrowDownRight, Star, BookmarkPlus, Bookmark,
  LineChart, Activity, Percent, Clock, Info, ExternalLink
} from 'lucide-react';

import { API } from '@/config/api';

// Sector icons
const SECTOR_ICONS = {
  diversified: Building2,
  retail: Building,
  infrastructure: Warehouse,
  industrial: Warehouse,
  data_centers: Building2,
  storage: Home,
};

export default function RealEstate() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('market');
  const [assets, setAssets] = useState([]);
  const [overview, setOverview] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [investments, setInvestments] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetDetail, setAssetDetail] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [assetsRes, overviewRes] = await Promise.all([
        fetch(`${API}/market/realestate/list`),
        fetch(`${API}/realestate/overview`)
      ]);

      if (assetsRes.ok) setAssets(await assetsRes.json());
      if (overviewRes.ok) setOverview(await overviewRes.json());

      // Fetch user-specific data if logged in
      if (user) {
        const [watchlistRes, investmentsRes] = await Promise.all([
          fetch(`${API}/realestate/watchlist`, { credentials: 'include' }),
          fetch(`${API}/realestate/investments`, { credentials: 'include' })
        ]);
        if (watchlistRes.ok) setWatchlist(await watchlistRes.json());
        if (investmentsRes.ok) setInvestments(await investmentsRes.json());
      }
    } catch (e) {
      console.error('Fetch error:', e);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchData();
    // Refresh every 60 seconds
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const fetchAssetDetail = async (symbol) => {
    try {
      const res = await fetch(`${API}/realestate/detail/${symbol}`);
      if (res.ok) {
        const data = await res.json();
        setAssetDetail(data);
      }
    } catch (e) {
      console.error('Detail fetch error:', e);
    }
  };

  const handleViewDetail = (asset) => {
    setSelectedAsset(asset);
    fetchAssetDetail(asset.symbol);
    setDetailModalOpen(true);
  };

  const addToWatchlist = async (symbol) => {
    try {
      const res = await fetch(`${API}/realestate/watchlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ symbol })
      });
      if (res.ok) {
        toast.success(`Added ${symbol} to watchlist`);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add');
      }
    } catch (e) {
      toast.error('Failed to add to watchlist');
    }
  };

  const removeFromWatchlist = async (symbol) => {
    try {
      const res = await fetch(`${API}/realestate/watchlist/${symbol}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success(`Removed ${symbol} from watchlist`);
        fetchData();
      }
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  const isInWatchlist = (symbol) => watchlist.some(w => w.symbol === symbol);

  const formatCurrency = (v) => {
    if (!v) return '$0.00';
    return `$${parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatLargeNumber = (v) => {
    if (!v) return 'N/A';
    const num = parseFloat(v);
    if (num >= 1e12) return `$${(num / 1e12).toFixed(1)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(1)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
    return formatCurrency(num);
  };

  const getSectorIcon = (type) => {
    const Icon = SECTOR_ICONS[type] || Building2;
    return Icon;
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white pt-16" data-testid="real-estate-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#14120f] via-[#1c1814] to-[#14120f] border-b border-[#1c1814]">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#D4AF37]/20 rounded-lg">
                <Building2 className="h-8 w-8 text-[#D4AF37]" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Real Estate Market</h1>
                <p className="text-gray-400">REITs, Real Estate ETFs & Property Investments</p>
              </div>
            </div>
            <Button onClick={fetchData} variant="outline" className="border-[#D4AF37]/50 text-[#D4AF37]">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Market Overview Cards */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-[#14120f] border-[#1c1814]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Total REITs</p>
                    <p className="text-2xl font-bold text-white">{overview.total_assets}</p>
                  </div>
                  <Building2 className="h-8 w-8 text-[#D4AF37]" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#14120f] border-[#1c1814]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Market Change</p>
                    <p className={`text-2xl font-bold ${overview.market_change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {overview.market_change >= 0 ? '+' : ''}{overview.market_change}%
                    </p>
                  </div>
                  {overview.market_change >= 0 ? 
                    <TrendingUp className="h-8 w-8 text-emerald-400" /> : 
                    <TrendingDown className="h-8 w-8 text-red-400" />
                  }
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#14120f] border-[#1c1814]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Top Gainer</p>
                    {overview.top_gainers?.[0] && (
                      <p className="text-lg font-bold text-emerald-400">
                        {overview.top_gainers[0].symbol} +{overview.top_gainers[0].change}%
                      </p>
                    )}
                  </div>
                  <ArrowUpRight className="h-8 w-8 text-emerald-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#14120f] border-[#1c1814]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Market Status</p>
                    <Badge className={overview.market_status === 'open' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'}>
                      {overview.market_status?.toUpperCase()}
                    </Badge>
                  </div>
                  <Clock className="h-8 w-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-[#14120f] border border-[#1c1814]">
            <TabsTrigger value="market" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <BarChart3 className="h-4 w-4 mr-2" /> Market
            </TabsTrigger>
            <TabsTrigger value="sectors" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <PieChart className="h-4 w-4 mr-2" /> Sectors
            </TabsTrigger>
            {user && (
              <>
                <TabsTrigger value="watchlist" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
                  <Bookmark className="h-4 w-4 mr-2" /> Watchlist ({watchlist.length})
                </TabsTrigger>
                <TabsTrigger value="portfolio" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
                  <DollarSign className="h-4 w-4 mr-2" /> Portfolio
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Market Tab */}
          <TabsContent value="market" className="space-y-4">
            <div className="grid gap-4">
              {loading ? (
                <div className="text-center py-12 text-gray-400">Loading real estate data...</div>
              ) : (
                <Card className="bg-[#14120f] border-[#1c1814]">
                  <CardContent className="p-0">
                    <table className="w-full">
                      <thead className="bg-[#0a0a0f]">
                        <tr className="text-gray-400 text-sm">
                          <th className="px-4 py-3 text-left">Asset</th>
                          <th className="px-4 py-3 text-left">Type</th>
                          <th className="px-4 py-3 text-right">Price</th>
                          <th className="px-4 py-3 text-right">Change</th>
                          <th className="px-4 py-3 text-right">Volume</th>
                          <th className="px-4 py-3 text-right">Source</th>
                          <th className="px-4 py-3 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assets.map((asset, index) => {
                          const SectorIcon = getSectorIcon(asset.type);
                          return (
                            <tr key={asset.symbol} className="border-t border-[#1c1814] hover:bg-[#1c1814]/50 transition-colors">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                                    <SectorIcon className="h-5 w-5 text-[#D4AF37]" />
                                  </div>
                                  <div>
                                    <p className="font-bold text-white">{asset.symbol}</p>
                                    <p className="text-xs text-gray-400">{asset.name}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <Badge variant="outline" className="border-gray-700 text-gray-400 capitalize">
                                  {asset.type?.replace('_', ' ') || 'REIT'}
                                </Badge>
                              </td>
                              <td className="px-4 py-3 text-right font-mono text-white">
                                {formatCurrency(asset.price)}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className={`font-mono flex items-center justify-end gap-1 ${asset.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {asset.change >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                                  {asset.change >= 0 ? '+' : ''}{asset.change?.toFixed(2)}%
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right text-gray-400 font-mono">
                                {asset.volume ? asset.volume.toLocaleString() : '-'}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <Badge className={asset.source === 'alpha_vantage' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'}>
                                  {asset.source === 'alpha_vantage' ? 'LIVE' : 'DEMO'}
                                </Badge>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-center gap-2">
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => handleViewDetail(asset)}
                                    className="text-[#D4AF37] hover:text-[#D4AF37]/80"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  {user && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => isInWatchlist(asset.symbol) ? removeFromWatchlist(asset.symbol) : addToWatchlist(asset.symbol)}
                                      className={isInWatchlist(asset.symbol) ? 'text-yellow-400' : 'text-gray-400'}
                                    >
                                      {isInWatchlist(asset.symbol) ? <Bookmark className="h-4 w-4 fill-current" /> : <BookmarkPlus className="h-4 w-4" />}
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Sectors Tab */}
          <TabsContent value="sectors" className="space-y-4">
            {overview?.sectors && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(overview.sectors).map(([sector, data]) => {
                  const SectorIcon = getSectorIcon(sector);
                  return (
                    <Card key={sector} className="bg-[#14120f] border-[#1c1814]">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="p-3 bg-[#D4AF37]/20 rounded-lg">
                            <SectorIcon className="h-6 w-6 text-[#D4AF37]" />
                          </div>
                          <div>
                            <h3 className="font-bold text-white capitalize">{sector.replace('_', ' ')}</h3>
                            <p className="text-sm text-gray-400">{data.count} assets</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">Avg Change</span>
                            <span className={data.avg_change >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                              {data.avg_change >= 0 ? '+' : ''}{data.avg_change}%
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">Total Value</span>
                            <span className="text-white">{formatCurrency(data.total_value)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Watchlist Tab */}
          <TabsContent value="watchlist" className="space-y-4">
            {watchlist.length === 0 ? (
              <Card className="bg-[#14120f] border-[#1c1814]">
                <CardContent className="p-12 text-center">
                  <Bookmark className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">No Watchlist Items</h3>
                  <p className="text-gray-400 mb-4">Add REITs to your watchlist to track them</p>
                  <Button onClick={() => setActiveTab('market')} className="bg-[#D4AF37] text-black">
                    Browse Market
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {watchlist.map(item => {
                  const asset = assets.find(a => a.symbol === item.symbol);
                  if (!asset) return null;
                  const SectorIcon = getSectorIcon(asset.type);
                  return (
                    <Card key={item.symbol} className="bg-[#14120f] border-[#1c1814]">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                              <SectorIcon className="h-5 w-5 text-[#D4AF37]" />
                            </div>
                            <div>
                              <p className="font-bold text-white">{asset.symbol}</p>
                              <p className="text-xs text-gray-400">{asset.name}</p>
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => removeFromWatchlist(item.symbol)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-2xl font-bold text-white">{formatCurrency(asset.price)}</span>
                          <span className={`font-mono flex items-center gap-1 ${asset.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {asset.change >= 0 ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                            {asset.change >= 0 ? '+' : ''}{asset.change?.toFixed(2)}%
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">Added: {new Date(item.added_at).toLocaleDateString()}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio" className="space-y-4">
            {!investments || investments.investments?.length === 0 ? (
              <Card className="bg-[#14120f] border-[#1c1814]">
                <CardContent className="p-12 text-center">
                  <DollarSign className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">No Real Estate Investments</h3>
                  <p className="text-gray-400 mb-4">Start investing in REITs to build your portfolio</p>
                  <Button onClick={() => setActiveTab('market')} className="bg-[#D4AF37] text-black">
                    Explore REITs
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Portfolio Summary */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-[#14120f] border-[#1c1814]">
                    <CardContent className="p-4">
                      <p className="text-gray-400 text-sm">Total Invested</p>
                      <p className="text-2xl font-bold text-white">{formatCurrency(investments.total_invested)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-[#14120f] border-[#1c1814]">
                    <CardContent className="p-4">
                      <p className="text-gray-400 text-sm">Current Value</p>
                      <p className="text-2xl font-bold text-[#D4AF37]">{formatCurrency(investments.total_current_value)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-[#14120f] border-[#1c1814]">
                    <CardContent className="p-4">
                      <p className="text-gray-400 text-sm">Total P&L</p>
                      <p className={`text-2xl font-bold ${investments.total_gain_loss >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {investments.total_gain_loss >= 0 ? '+' : ''}{formatCurrency(investments.total_gain_loss)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-[#14120f] border-[#1c1814]">
                    <CardContent className="p-4">
                      <p className="text-gray-400 text-sm">Return %</p>
                      <p className={`text-2xl font-bold ${investments.total_gain_loss_percent >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {investments.total_gain_loss_percent >= 0 ? '+' : ''}{investments.total_gain_loss_percent?.toFixed(2)}%
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Investment List */}
                <Card className="bg-[#14120f] border-[#1c1814]">
                  <CardContent className="p-0">
                    <table className="w-full">
                      <thead className="bg-[#0a0a0f]">
                        <tr className="text-gray-400 text-sm">
                          <th className="px-4 py-3 text-left">Asset</th>
                          <th className="px-4 py-3 text-right">Shares</th>
                          <th className="px-4 py-3 text-right">Avg Cost</th>
                          <th className="px-4 py-3 text-right">Current</th>
                          <th className="px-4 py-3 text-right">Value</th>
                          <th className="px-4 py-3 text-right">P&L</th>
                        </tr>
                      </thead>
                      <tbody>
                        {investments.investments?.map((inv, i) => (
                          <tr key={i} className="border-t border-[#1c1814]">
                            <td className="px-4 py-3 font-bold text-white">{inv.symbol}</td>
                            <td className="px-4 py-3 text-right text-white">{inv.shares}</td>
                            <td className="px-4 py-3 text-right text-gray-400">{formatCurrency(inv.purchase_price)}</td>
                            <td className="px-4 py-3 text-right text-white">{formatCurrency(inv.current_price)}</td>
                            <td className="px-4 py-3 text-right text-[#D4AF37]">{formatCurrency(inv.current_value)}</td>
                            <td className={`px-4 py-3 text-right ${inv.gain_loss >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {inv.gain_loss >= 0 ? '+' : ''}{formatCurrency(inv.gain_loss)} ({inv.gain_loss_percent?.toFixed(2)}%)
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="bg-[#14120f] border-[#1c1814] max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[#D4AF37]" />
              {assetDetail?.name || selectedAsset?.symbol}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {assetDetail?.sector} • {assetDetail?.industry}
            </DialogDescription>
          </DialogHeader>
          
          {selectedAsset && (
            <div className="space-y-4">
              {/* TradingView Chart */}
              <div className="bg-[#0a0a0f] rounded-lg overflow-hidden">
                <TradingViewWidget 
                  symbol={`AMEX:${selectedAsset.symbol}`} 
                  theme="dark" 
                  height={300}
                />
              </div>

              {/* Price Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-[#0a0a0f] rounded-lg">
                  <p className="text-xs text-gray-500">Price</p>
                  <p className="text-lg font-bold text-white">{formatCurrency(assetDetail?.price || selectedAsset.price)}</p>
                </div>
                <div className="p-3 bg-[#0a0a0f] rounded-lg">
                  <p className="text-xs text-gray-500">Change</p>
                  <p className={`text-lg font-bold ${(assetDetail?.change || selectedAsset.change) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {assetDetail?.change_percent || `${selectedAsset.change?.toFixed(2)}%`}
                  </p>
                </div>
                <div className="p-3 bg-[#0a0a0f] rounded-lg">
                  <p className="text-xs text-gray-500">Volume</p>
                  <p className="text-lg font-bold text-white">{assetDetail?.volume?.toLocaleString() || 'N/A'}</p>
                </div>
                <div className="p-3 bg-[#0a0a0f] rounded-lg">
                  <p className="text-xs text-gray-500">Market Cap</p>
                  <p className="text-lg font-bold text-white">{formatLargeNumber(assetDetail?.market_cap)}</p>
                </div>
              </div>

              {/* Additional Details */}
              {assetDetail && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-[#0a0a0f] rounded-lg">
                    <p className="text-xs text-gray-500">P/E Ratio</p>
                    <p className="text-white">{assetDetail.pe_ratio || 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-[#0a0a0f] rounded-lg">
                    <p className="text-xs text-gray-500">Dividend Yield</p>
                    <p className="text-emerald-400">{assetDetail.dividend_yield ? `${(parseFloat(assetDetail.dividend_yield) * 100).toFixed(2)}%` : 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-[#0a0a0f] rounded-lg">
                    <p className="text-xs text-gray-500">52W High</p>
                    <p className="text-white">{formatCurrency(assetDetail['52_week_high'])}</p>
                  </div>
                  <div className="p-3 bg-[#0a0a0f] rounded-lg">
                    <p className="text-xs text-gray-500">52W Low</p>
                    <p className="text-white">{formatCurrency(assetDetail['52_week_low'])}</p>
                  </div>
                </div>
              )}

              {/* Description */}
              {assetDetail?.description && (
                <div className="p-4 bg-[#0a0a0f] rounded-lg">
                  <p className="text-sm text-gray-400">{assetDetail.description.slice(0, 300)}...</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {user && !isInWatchlist(selectedAsset?.symbol) && (
              <Button 
                onClick={() => { addToWatchlist(selectedAsset.symbol); setDetailModalOpen(false); }}
                className="bg-[#D4AF37] text-black"
              >
                <BookmarkPlus className="h-4 w-4 mr-2" /> Add to Watchlist
              </Button>
            )}
            <Button variant="outline" onClick={() => setDetailModalOpen(false)} className="border-gray-700 text-gray-400">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
